import "./legal-document-viewer.less";

import {t} from "@lingui/core/macro";
import {StyleSheet, type CSSProperties} from "aphrodite";
import {useMemo, type ReactElement} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {Errors, KAError} from "@repo/logging";

import {useLegalDocBySlugQuery} from "./use-fetch-legal-doc-by-slug.ts";

type Props = {
    /**
     * The slug string of the legal document you wish to render
     */
    slug: string;
    /**
     * Any styles passed in here will override existing innate styles on the component
     */
    styles?: CSSProperties;
};

export const LegalDocumentViewer = ({
    slug,
    styles = {},
}: Props): ReactElement | null => {
    const [result] = useLegalDocBySlugQuery({
        slug,
    });
    const content = useMemo(() => {
        return result.status === "success" && result.data.legalDocumentContent
            ? result.data.legalDocumentContent?.content
            : "";
    }, [result]);

    try {
        if (result.status === "error") {
            throw new KAError(
                `Error with query for fetching legal doc by slug: ${result.error.message}`,
                Errors.Internal,
            );
        }
    } catch (e) {
        return (
            <View style={[innateStyle.container, styles]}>
                <Body>
                    {t`There's been an error displaying this document. Please try again later.`}
                </Body>
            </View>
        );
    }

    if (result.status === "loading") {
        return <CircularSpinner />;
    }

    return (
        <View
            style={[innateStyle.container, styles]}
            className="legal-document-viewer"
        >
            <div dangerouslySetInnerHTML={{__html: content}} />
        </View>
    );
};

const innateStyle = StyleSheet.create({
    container: {
        maxWidth: "600px",
        padding: `${spacing.large_24}px`,
        margin: "0 auto",
    },
});
